// quick-add-bulk.js

if (!customElements.get('quick-add-bulk')) {
  customElements.define(
    'quick-add-bulk',
    class QuickAddBulk extends BulkAdd {
      constructor() {
        super();
        this.quantity = this.querySelector('quantity-input');

        const debouncedOnChange = debounce((event) => {
          if (parseInt(event.target.value) === 0) {
            this.startQueue(event.target.dataset.index, parseInt(event.target.value));
          } else {
            this.validateQuantity(event);
          }
        }, ON_CHANGE_DEBOUNCE_TIMER);

        this.addEventListener('change', debouncedOnChange.bind(this));
        this.listenForActiveInput();
        this.listenForKeydown();
        this.lastActiveInputId = null;
        this.isEnterPressed = false;
      }

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (
            event.source === 'quick-add' ||
            (event.cartData.items && !event.cartData.items.some((item) => item.id === parseInt(this.dataset.index))) ||
            (event.cartData.variant_id && !(event.cartData.variant_id === parseInt(this.dataset.index)))
          ) {
            return;
          }

          this.onCartUpdate().then(() => {
            this.listenForActiveInput();
            this.listenForKeydown();
          });
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      get input() {
        return this.querySelector('quantity-input input');
      }

      selectProgressBar() {
        return this.querySelector('.progress-bar-container');
      }

      listenForActiveInput() {
        if (!this.classList.contains('hidden')) {
          this.input?.addEventListener('focusin', (event) => event.target.select());
        }
        this.isEnterPressed = false;
      }

      listenForKeydown() {
        this.input?.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            this.input?.blur();
            this.isEnterPressed = true;
          }
        });
      }

      cleanErrorMessageOnType(event) {
        event.target.addEventListener(
          'keypress',
          () => {
            event.target.setCustomValidity('');
          },
          { once: true }
        );
      }

      // ------- Hardened sectionId getter -------
      get sectionId() {
        if (this._sectionId !== undefined) return this._sectionId;

        // Try original wrapper first, then common Shopify/Dawn patterns
        const section =
          this.closest?.('.collection-quick-add-bulk') ||
          this.closest?.('[data-section-id]') ||
          this.closest?.('section[id^="shopify-section-"]') ||
          null;

        if (!section) {
          console.warn('[QuickAddBulk] No section wrapper found for', this);
          this._sectionId = null;
          return this._sectionId;
        }

        // Prefer explicit data attribute if present, else derive from id
        const fromData = section.dataset?.id || section.dataset?.sectionId || null;
        const fromId = section.id ? section.id.replace(/^shopify-section-/, '') : null;

        this._sectionId = fromData ?? fromId ?? null;
        return this._sectionId;
      }

      onCartUpdate() {
        const sid = this.sectionId;
        if (!sid) {
          console.warn('[QuickAddBulk] onCartUpdate skipped: missing sectionId');
          return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
          fetch(`${this.getSectionsUrl()}?section_id=${sid}`)
            .then((response) => response.text())
            .then((responseText) => {
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              const sourceQty = html.querySelector(`#quick-add-bulk-${this.dataset.index}-${sid}`);
              if (sourceQty) {
                this.innerHTML = sourceQty.innerHTML;
              }
              resolve();
            })
            .catch((e) => {
              console.error(e);
              reject(e);
            });
        });
      }

      getSectionsUrl() {
        const pageParams = new URLSearchParams(window.location.search);
        const pageNumber = decodeURIComponent(pageParams.get('page') || '');

        return `${window.location.pathname}${pageNumber ? `?page=${pageNumber}` : ''}`;
      }

      updateMultipleQty(items) {
        this.selectProgressBar()?.classList.remove('hidden');

        const sid = this.sectionId;
        if (!sid) {
          console.warn('[QuickAddBulk] updateMultipleQty aborted: missing sectionId');
          this.selectProgressBar()?.classList.add('hidden');
          this.setRequestStarted(false);
          return;
        }

        const ids = Object.keys(items);
        const body = JSON.stringify({
          updates: items,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: this.getSectionsUrl(),
        });

        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
          .then((response) => response.text())
          .then((state) => {
            const parsedState = JSON.parse(state);
            this.renderSections(parsedState, ids);
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-add', cartData: parsedState });
          })
          .catch(() => {
            // Commented out for now and will be fixed when BE issue is done https://github.com/Shopify/shopify/issues/440605
            // e.target.setCustomValidity(error);
            // e.target.reportValidity();
            // this.resetQuantityInput(ids[index]);
            // this.selectProgressBar().classList.add('hidden');
            // e.target.select();
            // this.cleanErrorMessageOnType(e);
          })
          .finally(() => {
            this.selectProgressBar()?.classList.add('hidden');
            this.setRequestStarted(false);
          });
      }

      getSectionsToRender() {
        const sid = this.sectionId;
        if (!sid) return []; // guard to prevent null deref

        return [
          {
            id: `quick-add-bulk-${this.dataset.index}-${sid}`,
            section: sid,
            selector: `#quick-add-bulk-${this.dataset.index}-${sid}`,
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
          {
            id: 'CartDrawer',
            selector: '.drawer__inner',
            section: 'cart-drawer',
          },
        ];
      }

      renderSections(parsedState, ids) {
        const intersection = this.queue.filter((element) => ids.includes(element.id));
        if (intersection.length !== 0) return;

        this.getSectionsToRender().forEach((section) => {
          const sectionElement = document.getElementById(section.id);
          if (!sectionElement) return;

          if (section.section === 'cart-drawer') {
            sectionElement
              .closest('cart-drawer')
              ?.classList.toggle('is-empty', parsedState.items.length === 0); // fixed .length.length bug
          }

          const elementToReplace =
            sectionElement && sectionElement.querySelector(section.selector)
              ? sectionElement.querySelector(section.selector)
              : sectionElement;

          if (elementToReplace && parsedState.sections && parsedState.sections[section.section]) {
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          }
        });

        if (this.isEnterPressed) {
          this.querySelector?.(`#Quantity-${this.lastActiveInputId}`)?.select?.();
        }

        this.listenForActiveInput();
        this.listenForKeydown();
      }
    }
  );
}